import { find, get, startCase } from 'lodash';

import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { formatInt } from 'shared/utils/numberFormatters';
import { EntryMode, ExpenseHistory, InputMethod } from 'types/underwriting/income-approach/expenses';
import { Property } from 'types/underwriting/property';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { financingTermsFormHelpers } from '../financing-terms/financingTermsFormConfig';
import { ExpenseCategory } from '../income-approach/expenses/types';
import { divide } from 'shared/utils/numberOperations';
import { EXPENSE_AMOUNT_INPUT_METHOD, EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';
import { calculateOperatingExpenses } from '../income-approach/expenses/operating-expenses/expense-history/helpers';
import { EXPENSE_AMOUNT_LABEL, EXPENSE_RATIO_LABEL } from '../income-approach/expenses/constants';

interface ExpenseRow {
  id: string;
  category: string;
  isTotal?: boolean;
  average: number;
  [key: string]: any;
}

export const getPropertyData = (property: Property) => {
  const data: any = {
    address: property.address.street,
    city: property.address.city,
    state: property.address.state,
    zip: property.address.zip,
    propertyType: startCase(property.propertyType),
    grossBuildingArea: formatInt(property.grossBuildingArea),
  };
  if (property.residentialUnits) {
    data.residentialUnits = property.residentialUnits;
  }
  if (property.commercialUnits) {
    data.commercialUnits = property.commercialUnits;
  }
  return data;
};

const getDetailedExpenseHistoryData = (expenseHistory: ExpenseHistory[], expenseCategories: ExpenseCategory[]) => {
  const totalValues: any = {};
  const totalExcludingREValues: any = {};

  const rows = expenseCategories.map((row: ExpenseCategory) => {
    const updatedRow: any = { id: row.key, ...row };
    const yearValues: number[] = [];

    expenseHistory.forEach((expenseHistoryItem: ExpenseHistory) => {
      const expenses = expenseHistoryItem.expenses || [];
      const fieldName = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;
      const category = row.category;
      const expenseValue = find(expenses, (expense) => expense.category === category)?.value || 0;
      updatedRow[fieldName] = expenseValue;
      yearValues.push(expenseValue);
      const total = expenses?.reduce((sum: number, expense: any) => sum + (expense.value || 0), 0) || 0;
      const totalExcludingRE =
        expenses?.reduce((sum: number, expense: any) => {
          if (expense.category === 'Real Estate Taxes') return sum;
          return sum + (expense.value || 0);
        }, 0) || 0;

      totalValues[fieldName] = total;
      totalExcludingREValues[fieldName] = totalExcludingRE;
    });

    const yearCount = expenseHistory.length || 1;

    updatedRow.average =
      divide(
        yearValues.reduce((sum, val) => sum + val, 0),
        yearCount
      ) || 0;
    return updatedRow;
  });

  const totalRow: ExpenseRow = {
    id: 'total-operating-expenses',
    category: 'Total Operating Expenses',
    isTotal: true,
    average: 0,
  };
  const totalExcludingRETaxesRow: ExpenseRow = {
    id: 'total-excluding-re-taxes',
    category: 'Total Operating Expenses (Excl. RE Taxes)',
    isTotal: true,
    average: 0,
  };
  Object.keys(totalValues).forEach((key: string) => {
    totalRow[key] = get(totalValues, key, 0);
    totalExcludingRETaxesRow[key] = get(totalExcludingREValues, key, 0);
  });
  const yearCount = expenseHistory.length || 1;

  totalRow.average =
    divide(
      Object.values<number>(totalValues).reduce((sum, val) => sum + val, 0),
      yearCount
    ) || 0;

  totalExcludingRETaxesRow.average =
    divide(
      Object.values<number>(totalExcludingREValues).reduce((sum, val) => sum + val, 0),
      yearCount
    ) || 0;

  return {
    rows,
    totalRow,
    totalExcludingRETaxesRow,
  };
};

const getSummarizedExpenseHistoryData = (
  expenseHistory: ExpenseHistory[],
  effectiveGrossIncome: number,
  inputMethod: InputMethod
) => {
  const yearCount = expenseHistory.length || 1;

  const id = inputMethod === EXPENSE_AMOUNT_INPUT_METHOD ? 'operating-expenses-amount' : 'operating-expenses-ratio';
  const expenseValues: number[] = [];
  const totalValues: number[] = [];

  const expenseRow: ExpenseRow = {
    id,
    key: id,
    category: inputMethod === EXPENSE_AMOUNT_INPUT_METHOD ? EXPENSE_AMOUNT_LABEL : EXPENSE_RATIO_LABEL,
    isTotal: false,
    average: 0,
  };

  const totalOperatingExpenses: ExpenseRow = {
    id: 'total-operating-expenses',
    category: 'Total Operating Expenses',
    isTotal: true,
    average: 0,
  };

  expenseHistory.forEach((expenseHistoryItem: ExpenseHistory) => {
    const yearKey = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;

    const expenseValue =
      inputMethod === EXPENSE_AMOUNT_INPUT_METHOD
        ? expenseHistoryItem.totalExpenseAmount
        : expenseHistoryItem.operatingExpenseRatio;

    const { amount, ratio } = calculateOperatingExpenses(effectiveGrossIncome, expenseValue || 0, inputMethod);

    const totalValue = inputMethod === EXPENSE_AMOUNT_INPUT_METHOD ? ratio : amount;

    expenseRow[yearKey] = expenseValue || 0;
    totalOperatingExpenses[yearKey] = totalValue || 0;

    expenseValues.push(expenseValue || 0);
    totalValues.push(totalValue || 0);
  });

  expenseRow.average =
    divide(
      expenseValues.reduce((sum, val) => sum + val, 0),
      yearCount
    ) || 0;

  totalOperatingExpenses.average =
    divide(
      totalValues.reduce((sum, val) => sum + val, 0),
      yearCount
    ) || 0;

  return {
    rows: [expenseRow],
    totalRow: totalOperatingExpenses,
  };
};

export const getExpenseHistoryData = (
  expenseHistory: ExpenseHistory[],
  expenseCategories: ExpenseCategory[],
  expenseHistoryEntryMode: EntryMode,
  expenseHistoryInputMethod: InputMethod,
  effectiveGrossIncome: number
): { yearColumns: string[]; rows: ExpenseRow[]; totalRow: ExpenseRow; totalExcludingRETaxesRow?: ExpenseRow } => {
  const isSummarized = expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE;

  const allKeys = new Set<string>();
  expenseHistory.forEach((expense: ExpenseHistory) => {
    const { year, period } = expense;
    const key = `${period}-${year}`;
    allKeys.add(key);
  });
  const yearColumns = Array.from(allKeys).sort();

  const tableData = isSummarized
    ? getSummarizedExpenseHistoryData(expenseHistory, effectiveGrossIncome, expenseHistoryInputMethod)
    : getDetailedExpenseHistoryData(expenseHistory, expenseCategories);

  return {
    yearColumns,
    ...tableData,
  };
};

export const getFinancingTermsData = (underwritingData: UnderwritingOutput) => {
  const { financingTerms = {} } = underwritingData;
  const borrowerNetOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );
  const underwriterNetOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.projected
  );
  const hypotheticalNetOperatingIncome = financingTerms.hypothetical?.netOperatingIncome;

  const {
    capRate: borrowerCapRate = 0,
    loanToValue: borrowerLoanToValue = 0,
    interestRate: borrowerInterestRate = 0,
    loanTerm: borrowerLoanTerm = 0,
  } = financingTerms.borrower || {};
  const {
    capRate: underwriterCapRate = 0,
    loanToValue: underwriterLoanToValue = 0,
    interestRate: underwriterInterestRate = 0,
    loanTerm: underwriterLoanTerm = 0,
  } = financingTerms.underwriter || {};
  const {
    capRate: hypotheticalCapRate = 0,
    loanToValue: hypotheticalLoanToValue = 0,
    interestRate: hypotheticalInterestRate = 0,
    loanTerm: hypotheticalLoanTerm = 0,
  } = financingTerms.hypothetical || {};

  const borrowerLoanAmount = financingTermsFormHelpers.calculateLoanAmount(
    borrowerNetOperatingIncome,
    borrowerCapRate,
    borrowerLoanToValue
  );
  const underwriterLoanAmount = financingTermsFormHelpers.calculateLoanAmount(
    underwriterNetOperatingIncome,
    underwriterCapRate,
    underwriterLoanToValue
  );
  const hypotheticalLoanAmount = financingTermsFormHelpers.calculateLoanAmount(
    hypotheticalNetOperatingIncome || 0,
    hypotheticalCapRate,
    hypotheticalLoanToValue
  );

  const borrowerDebtService = financingTermsFormHelpers.calculateDebtService(
    borrowerLoanAmount,
    borrowerInterestRate,
    borrowerLoanTerm
  );
  const underwriterDebtService = financingTermsFormHelpers.calculateDebtService(
    underwriterLoanAmount,
    underwriterInterestRate,
    underwriterLoanTerm
  );
  const hypotheticalDebtService = financingTermsFormHelpers.calculateDebtService(
    hypotheticalLoanAmount || 0,
    hypotheticalInterestRate,
    hypotheticalLoanTerm
  );

  const borrowerDebtServiceCoverageRatio = financingTermsFormHelpers.calculateDebtServiceCoverageRatio(
    borrowerNetOperatingIncome,
    borrowerDebtService
  );
  const underwriterDebtServiceCoverageRatio = financingTermsFormHelpers.calculateDebtServiceCoverageRatio(
    underwriterNetOperatingIncome,
    underwriterDebtService
  );
  const hypotheticalDebtServiceCoverageRatio = financingTermsFormHelpers.calculateDebtServiceCoverageRatio(
    hypotheticalNetOperatingIncome || 0,
    hypotheticalDebtService
  );

  const data: any[] = [
    {
      item: 'Net Operating Income',
      borrower: borrowerNetOperatingIncome,
      underwriter: underwriterNetOperatingIncome,
      hypothetical: hypotheticalNetOperatingIncome,
    },
    {
      item: 'Cap Rate',
      borrower: borrowerCapRate,
      underwriter: underwriterCapRate,
      hypothetical: hypotheticalCapRate,
    },
    {
      item: 'Appraised Value',
      borrower: financingTermsFormHelpers.calculateAppraisedValue(borrowerNetOperatingIncome, borrowerCapRate),
      underwriter: financingTermsFormHelpers.calculateAppraisedValue(underwriterNetOperatingIncome, underwriterCapRate),
      hypothetical: financingTermsFormHelpers.calculateAppraisedValue(
        hypotheticalNetOperatingIncome || 0,
        hypotheticalCapRate || 0
      ),
    },
    {
      item: 'Loan to Value (LTV)',
      borrower: borrowerLoanToValue,
      underwriter: underwriterLoanToValue,
      hypothetical: hypotheticalLoanToValue,
    },
    {
      item: 'Loan Amount',
      borrower: borrowerLoanAmount,
      underwriter: underwriterLoanAmount,
      hypothetical: financingTermsFormHelpers.calculateLoanAmount(
        hypotheticalNetOperatingIncome || 0,
        hypotheticalCapRate,
        hypotheticalLoanToValue
      ),
    },
    {
      item: 'Interest Rate',
      borrower: borrowerInterestRate,
      underwriter: underwriterInterestRate,
      hypothetical: hypotheticalInterestRate,
    },
    {
      item: 'Loan Term (Years)',
      borrower: borrowerLoanTerm,
      underwriter: underwriterLoanTerm,
      hypothetical: hypotheticalLoanTerm,
    },
    {
      item: 'Debt Service',
      borrower: financingTermsFormHelpers.calculateDebtService(
        borrowerLoanAmount,
        borrowerInterestRate,
        borrowerLoanTerm
      ),
      underwriter: financingTermsFormHelpers.calculateDebtService(
        underwriterLoanAmount,
        underwriterInterestRate,
        underwriterLoanTerm
      ),
      hypothetical: financingTermsFormHelpers.calculateDebtService(
        hypotheticalLoanAmount || 0,
        hypotheticalInterestRate,
        hypotheticalLoanTerm
      ),
    },
    {
      item: 'Debt Service Coverage Ratio (DSCR)',
      borrower: borrowerDebtServiceCoverageRatio,
      underwriter: underwriterDebtServiceCoverageRatio,
      hypothetical: hypotheticalDebtServiceCoverageRatio,
    },
  ];

  return data;
};
